import { css } from '@emotion/react'
import { NextPage } from 'next'
import { ParsedUrlQuery } from 'querystring'
import * as React from 'react'
import { RoundsHead } from '@/components/common/Head'
import { Pagination } from '@/components/common/Pagination'
import { Template } from '@/components/common/Template'
import { DownloadButton } from '@/components/pages/rounds/DownloadButton'
import { FundingRoundViewResultTable } from '@/components/pages/rounds/FundingRoundViewResultTable'
import {
  OpeningCondition,
  SearchConditionList,
} from '@/components/pages/rounds/SearchConditionList'
import { SeriesViewResultTable } from '@/components/pages/rounds/SeriesViewResultTable'
import { useUser } from '../../contexts/AuthContext'
import {
  downloadReport,
  getFundingRoundsSSR,
  Order,
  Sort,
  SearchConditions,
} from '@/lib/api/round'
import { formatWithComma } from '@/lib/number'
import { isEnterprise, isLatestPlan, isTrial } from '@/lib/user'
import { Round } from '../../types'
import { FundingRoundData } from '../../types/api'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from '@/components/atoms/Breadcrumb'
import { EnterpriseGuideTemplate } from '@/components/view/EnterpriseGuide'

export type ViewMode = 'series' | 'funding_round'

export type RoundsInitialCondition = {
  keywords?: string
  initialSeries?: string
  from?: string
  to?: string
  amountFrom?: number
  amountTo?: number
  postMoneyFrom?: number
  postMoneyTo?: number
  sort?: string
  order?: string
}

type Props = {
  fundingRounds: FundingRoundData[]
  count: number
  initialCondition: RoundsInitialCondition
  searchConditions?: SearchConditions
}

const RoundsPage: NextPage<Props> = (props) => {
  const user = useUser()
  const [rounds, setRounds] = React.useState<Round[]>([])
  const [fundingRounds, setFundingRounds] = React.useState<FundingRoundData[]>(
    props.fundingRounds
  )
  const [count, setCount] = React.useState<number | null>(props.count)
  const [page, setPage] = React.useState<number>(1)
  const [openingCondition, setOpeningCondition] =
    React.useState<OpeningCondition>(null)
  const [sort, setSort] = React.useState<Sort>(
    (props.initialCondition.sort as Sort) ?? null
  )
  const [order, setOrder] = React.useState<Order>(
    (props.initialCondition.order as Order) ?? null
  )
  const [isLoading, setIsLoading] = React.useState(false)
  const toggleSort = (selectedSort: Sort) => {
    if (sort === selectedSort) {
      if (order === 'desc') {
        setOrder('asc')
      } else {
        setSort(null)
        setOrder(null)
      }
    } else {
      setSort(selectedSort)
      setOrder('desc')
    }
  }

  const [view, setView] = React.useState<ViewMode>('funding_round')

  const [searchConditions, setSearchConditions] =
    React.useState<SearchConditions>(
      props.searchConditions
        ? props.searchConditions
        : { keywords: '', initialSeries: '' }
    )

  return isEnterprise(user) || isTrial(user) ? (
    <Template>
      <RoundsHead />
      <div
        className="rounds-page"
        onClick={() => {
          setOpeningCondition(null)
        }}
      >
        <div>
          <div
            css={css`
              width: 78%;
              margin: 16px auto 20px;
            `}
          >
            <div
              css={css`
                margin-bottom: 16px;
              `}
            >
              <Breadcrumb>
                <BreadcrumbLink href="/">ホーム</BreadcrumbLink>
                <BreadcrumbItem>資金調達ラウンド</BreadcrumbItem>
              </Breadcrumb>
            </div>
            <SearchConditionList
              setRounds={setRounds}
              setFundingRounds={setFundingRounds}
              setCount={setCount}
              page={page}
              setPage={setPage}
              openingCondition={openingCondition}
              setOpeningCondition={setOpeningCondition}
              sort={sort}
              order={order}
              view={view}
              setView={setView}
              initialCondition={props.initialCondition}
              setSearchConditions={setSearchConditions}
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            margin: 0 25px 10px;
          `}
        >
          <div
            css={css`
              font-size: 14px;
            `}
          >
            検索結果:
            <span
              css={css`
                font-size: 24px;
                margin: 0 3px;
              `}
            >
              {formatWithComma(count)}
            </span>
            件
          </div>
          {isEnterprise(user) && view === 'funding_round' && (
            <DownloadButton
              className={
                isLoading ? 'downloadButton isLoading' : 'downloadButton'
              }
              disabled={
                !(isEnterprise(user) && isLatestPlan(user)) || isLoading
              }
              isLoading={isLoading}
              count={count}
              onClick={() => downloadReport(setIsLoading, searchConditions)}
            />
          )}
        </div>
        {view === 'series' ? (
          <SeriesViewResultTable
            rounds={rounds}
            sort={sort}
            order={order}
            toggleSort={toggleSort}
          />
        ) : (
          <FundingRoundViewResultTable
            fundingRounds={fundingRounds}
            sort={sort}
            order={order}
            toggleSort={toggleSort}
          />
        )}
        <Pagination
          page={page}
          setPage={setPage}
          count={count}
          limit={50}
          css={css`
            margin-bottom: 50px;
          `}
        ></Pagination>
      </div>
    </Template>
  ) : (
    <EnterpriseGuideTemplate />
  )
}

RoundsPage.getInitialProps = async (ctx) => {
  const res = await getFundingRoundsSSR(ctx)
  const initialCondition = parseQuery(ctx.query)
  return {
    initialCondition,
    fundingRounds: res.data,
    count: res.count,
  }
}

const parseQuery = (query: ParsedUrlQuery): RoundsInitialCondition => {
  const parseNum = (v?: string | string[]) => {
    const parsed = parseInt(v?.toString() ?? '', 10)
    return isNaN(parsed) ? undefined : parsed
  }

  return {
    keywords: query.keywords?.toString(),
    initialSeries: query.initialSeries?.toString(),
    from: query.from?.toString(),
    to: query.to?.toString(),
    amountFrom: parseNum(query.amountFrom),
    amountTo: parseNum(query.amountTo),
    postMoneyFrom: parseNum(query.postMoneyFrom),
    postMoneyTo: parseNum(query.postMoneyTo),
    sort: query.sort?.toString(),
    order: query.sort?.toString(),
  }
}

export default RoundsPage
